/**
 * Hooks React Query da Tela 8 (Iniciar Conversa Proativa) — Brief 2B B7.
 *
 * Templates e contexto são leitura (cacheável); a iniciação é mutation.
 */

import { useMutation, useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api-client';

import type {
  ProactiveTemplate,
  ProactiveContext,
  ContextOriginType,
  InitiateProactivePayload,
  InitiateProactiveResponse,
  RecipientStatus,
} from './types';

const STALE = 30 * 1000;

export function useProactiveTemplates(category: string | undefined): {
  data: ProactiveTemplate[] | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ['proactive-templates', category],
    queryFn: () => api.listProactiveTemplates(category),
    enabled: category !== undefined && category !== '',
    staleTime: STALE,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

export function useProactiveContext(
  originType: ContextOriginType | undefined,
  originId: string | undefined,
): {
  data: ProactiveContext | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const enabled = originType !== undefined && originId !== undefined && originId !== '';
  const query = useQuery({
    queryKey: ['proactive-context', originType, originId],
    queryFn: () => api.resolveProactiveContext(originType!, originId!),
    enabled,
    staleTime: STALE,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

/**
 * R-G9 — o estado de restrição do destinatário, para o wizard avisar ANTES do Passo 4.
 *
 * `enabled` gateia o FETCH (não só o render): sem pessoa selecionada não há pergunta a fazer.
 *
 * ★ Fail-open DELIBERADO: `isError` NÃO bloqueia o wizard. O fail-closed vive na PORTA (o 422 do
 * POST, server-authoritative); um aviso que travasse por falha de rede viraria negação silenciosa
 * de trabalho legítimo. A direção é assimétrica de propósito.
 *
 * ★ `staleTime: 0` CONTRA o default de 5 min do QueryClient, e o motivo é a direção do erro: um
 * positivo em cache («está restrita») envelhecido diria ao operador que não pode procurar alguém
 * que acabou de revogar a restrição. O projeto tem `refetchOnWindowFocus: false`, então um stale
 * positivo NÃO expiraria sozinho enquanto o wizard estivesse aberto. A leitura é 1 query barata,
 * gateada por pessoa selecionada — frescor vale mais que o cache aqui.
 */
export function useRecipientStatus(personId: string | undefined): {
  data: RecipientStatus | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ['proactive-recipient-status', personId],
    queryFn: () => api.getProactiveRecipientStatus(personId!),
    enabled: personId !== undefined && personId !== '',
    staleTime: 0,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

export function useInitiateProactive(): {
  mutateAsync: (payload: InitiateProactivePayload) => Promise<InitiateProactiveResponse>;
  isPending: boolean;
} {
  const mutation = useMutation({
    mutationFn: (payload: InitiateProactivePayload) => api.initiateProactiveConversation(payload),
  });
  return { mutateAsync: mutation.mutateAsync, isPending: mutation.isPending };
}
